import React, { useState, useEffect, useMemo } from "react";
import {
  loadProcedureRegistry,
  getGroupedProcedures,
  getTargetSelector,
  reloadProcedureRegistry,
  type ProcedureEntry
} from "../../../registry/procedureRegistry";
import { apiGet } from "../../../services/api";
import { fdiToIso, isFdiKey, isoToFdi } from "../../../utils/toothNotation";
import Button from "../../common/Button";
import "./ProcedureLauncher.css";

interface PendingSession {
  id: string;
  name?: string;
  procedure_type?: string;
  tooth_no?: string;
}

interface PlannedProcedure {
  plan_id: number;
  item_id: number;
  master_id: string; // The backend uses procedure_id which maps to master_id here
  procedure_name: string;
  tooth_no?: string;
}

type GroupedProceduresMap = Record<string, ProcedureEntry[]>;

export interface ProcedureLauncherProps {
  activeTooth?: string | null;
  activeTeeth?: string[];
  onStart: (opts: {
    master_id?: string;
    targetTooth?: string | null;
    targetTeeth?: string[];
    surface?: string | null;
    diagnosisCode?: string | null;
    resumeSessionId?: string | number;
  }) => void;
  isLocked?: boolean;
  patientId?: string | null;
}

export default function ProcedureLauncher({
  activeTooth,
  activeTeeth = [],
  onStart,
  isLocked,
  patientId
}: ProcedureLauncherProps) {
  const [groupedProcedures, setGroupedProcedures] =
    useState<GroupedProceduresMap>({});
  const [loading, setLoading] = useState(true);
  const [pendingSessions, setPendingSessions] =
    useState<PendingSession[]>([]);
  const [plannedProcedures, setPlannedProcedures] =
    useState<PlannedProcedure[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [toothPromptProc, setToothPromptProc] =
    useState<ProcedureEntry | null>(null);
  const [manualToothInput, setManualToothInput] = useState("");

  const anteriorFdi = ["13", "12", "11", "21", "22", "23", "31", "32", "33", "41", "42", "43"];
  const anteriorIso = new Set(anteriorFdi.map(fdiToIso));

  const normalizeToIso = (tooth: string) => {
    const trimmed = tooth.trim();
    return isFdiKey(trimmed) ? fdiToIso(trimmed) : trimmed;
  };

  const classifyRctSelection = (teeth: string[]) => {
    if (!teeth.length) return "none";
    const isoTeeth = teeth.map(normalizeToIso);
    const isAnterior = isoTeeth.map((t) => anteriorIso.has(t));
    if (isAnterior.every(Boolean)) return "anterior";
    if (isAnterior.every((v) => !v)) return "posterior";
    return "mixed";
  };

  /* ---------------- Load procedure registry ---------------- */

  useEffect(() => {
    const loadProcedures = async () => {
      try {
        await loadProcedureRegistry();
        setGroupedProcedures(getGroupedProcedures());
      } catch (e) {
        console.error("Failed to load procedure registry", e);
        setGroupedProcedures({});
      } finally {
        setLoading(false);
      }
    };
    loadProcedures();
  }, []);

  /* ---------------- Load pending sessions ---------------- */

  useEffect(() => {
    const loadPending = async () => {
      try {
        const url = patientId
          ? `/api/doctor/procedures/pending?patient_id=${patientId}`
          : "/api/doctor/procedures/pending";
        const data = await apiGet(url);
        setPendingSessions(Array.isArray(data) ? (data as PendingSession[]) : []);
      } catch (e) {
        console.warn(
          "Could not load pending procedures",
          (e as Error).message
        );
        setPendingSessions([]);
      }
    };
    loadPending();
  }, [patientId]);

  /* ---------------- Load planned procedures ---------------- */

  useEffect(() => {
    const loadPlanned = async () => {
      if (!patientId) return;
      try {
        const url = `/api/treatment-plans/patient/${patientId}`;
        const data = await apiGet(url);
        const plans = Array.isArray(data) ? data : [];
        const acceptedPlans = plans.filter((p: any) => p.status === 'accepted');

        const extractedProcs: PlannedProcedure[] = [];
        acceptedPlans.forEach((plan: any) => {
          if (Array.isArray(plan.items)) {
            plan.items.forEach((item: any) => {
              extractedProcs.push({
                plan_id: plan.id,
                item_id: item.id,
                master_id: String(item.procedure_id),
                procedure_name: item.procedure_name,
                tooth_no: item.tooth_no
              });
            });
          }
        });
        setPlannedProcedures(extractedProcs);
      } catch (e) {
        console.warn("Could not load planned procedures", (e as Error).message);
        setPlannedProcedures([]);
      }
    };
    loadPlanned();
  }, [patientId]);

  /* ---------------- Common Procedures List ---------------- */
  const COMMON_PROCEDURES = [
    "SCALING_POLISHING", "COMPOSITE_RESTORATION", "GIC_RESTORATION",
    "EXTRACTION_SIMPLE", "RCT_ANTERIOR", "RCT_POSTERIOR",
    "CROWN_PFM", "CROWN_ZIRCONIA", "XRAY_IOPA"
  ];

  const filteredGrouped = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const out: GroupedProceduresMap = {};

    const allProcs = Object.values(groupedProcedures).flat();

    if (!q) {
      const common = allProcs.filter(p =>
        p.master_id && COMMON_PROCEDURES.some(id =>
          (p.master_id || "").includes(id) || (p.display_name || "").toUpperCase().includes(id)
        )
      );
      const uniqueCommon = Array.from(new Set(common.map(p => p.master_id)))
        .map(id => common.find(p => p.master_id === id))
        .filter((p): p is ProcedureEntry => !!p);

      if (uniqueCommon.length > 0) {
        out["⭐ Common"] = uniqueCommon;
      }
    }

    Object.entries(groupedProcedures).forEach(([cat, procs]) => {
      const filtered = procs.filter(
        (p) =>
          !q ||
          (p.display_name || p.name || "").toLowerCase().includes(q) ||
          (p.master_id || "").toLowerCase().includes(q)
      );
      if (filtered.length) out[cat] = filtered;
    });

    return out;
  }, [groupedProcedures, searchQuery]);

  const selectedTeethList = useMemo(() => {
    if (activeTeeth.length > 0) return activeTeeth;
    return activeTooth ? [activeTooth] : [];
  }, [activeTeeth, activeTooth]);

  const currentRctSelectionType = useMemo(
    () => classifyRctSelection(selectedTeethList),
    [selectedTeethList]
  );

  const handleStartProcedure = (proc: ProcedureEntry) => {
    if (!proc || !proc.master_id) return;

    const isRctAnterior =
      (proc.master_id || "").toUpperCase().includes("RCT_ANTERIOR") ||
      (proc.display_name || proc.name || "").toLowerCase().includes("anterior");
    const isRctPosterior =
      (proc.master_id || "").toUpperCase().includes("RCT_POSTERIOR") ||
      (proc.display_name || proc.name || "").toLowerCase().includes("posterior");
    
    if ((isRctAnterior || isRctPosterior) && currentRctSelectionType === "mixed") {
      alert("Select only anterior teeth or only posterior teeth for RCT.");
      return;
    }
    if (isRctAnterior && currentRctSelectionType === "posterior") {
      alert("Selected teeth are posterior. Choose RCT posterior.");
      return;
    }
    if (isRctPosterior && currentRctSelectionType === "anterior") {
      alert("Selected teeth are anterior. Choose RCT anterior.");
      return;
    }

    const targetSelector = getTargetSelector(proc.master_id);
    const requiresSingleTooth = proc.requires_tooth ?? targetSelector === "single_tooth";
    const requiresMultiTooth = targetSelector === "multi_tooth";

    if ((requiresSingleTooth || requiresMultiTooth) && !activeTooth && activeTeeth.length === 0) {
      setToothPromptProc(proc);
      setManualToothInput("");
      return;
    }

    onStart({
      master_id: proc.master_id,
      targetTooth: activeTooth ?? (activeTeeth.length === 1 ? activeTeeth[0] : null),
      targetTeeth: activeTeeth.length > 0 ? activeTeeth : activeTooth ? [activeTooth] : [],
      surface: null,
      diagnosisCode: null
    });
  };

  const handleToothPromptSubmit = () => {
    if (!toothPromptProc?.master_id) return;

    const raw = manualToothInput.trim().split(/[\s,]+/).map((t) => t.trim()).filter(Boolean);

    if (raw.length === 0) {
      alert("Enter at least one tooth number (e.g. 18 or 18, 22, 26).");
      return;
    }

    const isRctAnterior =
      (toothPromptProc.master_id || "").toUpperCase().includes("RCT_ANTERIOR") ||
      (toothPromptProc.display_name || toothPromptProc.name || "").toLowerCase().includes("anterior");
    const isRctPosterior =
      (toothPromptProc.master_id || "").toUpperCase().includes("RCT_POSTERIOR") ||
      (toothPromptProc.display_name || toothPromptProc.name || "").toLowerCase().includes("posterior");
    
    if (isRctAnterior || isRctPosterior) {
      const type = classifyRctSelection(raw);
      if (type === "mixed") {
        alert("Select only anterior teeth or only posterior teeth for RCT.");
        return;
      }
      if (isRctAnterior && type === "posterior") {
        alert("Selected teeth are posterior. Choose RCT posterior.");
        return;
      }
      if (isRctPosterior && type === "anterior") {
        alert("Selected teeth are anterior. Choose RCT anterior.");
        return;
      }
    }

    onStart({
      master_id: toothPromptProc.master_id,
      targetTooth: raw[0] ?? null,
      targetTeeth: raw,
      surface: null,
      diagnosisCode: null
    });

    setToothPromptProc(null);
    setManualToothInput("");
  };

  const getCategoryIcon = (category: string): string => {
    const icons: Record<string, string> = {
      "⭐ Common": "⭐",
      Endodontics: "⚡",
      Diagnostic: "🩺",
      Orthodontics: "🦷",
      "Oral Surgery": "🛠",
      Restorative: "✨",
      Prosthodontics: "👑",
      Implantology: "🔩",
      Periodontics: "💧",
      Preventive: "🛡",
      Emergency: "🚨"
    };
    return icons[category] || "⚕️";
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      "⭐ Common": "linear-gradient(135deg, #f59e0b, #fbbf24)",
      Endodontics: "linear-gradient(135deg, #f97316, #fb923c)",
      Diagnostic: "linear-gradient(135deg, #3b82f6, #60a5fa)",
      Orthodontics: "linear-gradient(135deg, #8b5cf6, #a78bfa)",
      "Oral Surgery": "linear-gradient(135deg, #ef4444, #f87171)",
      Restorative: "linear-gradient(135deg, #22c55e, #4ade80)",
      Prosthodontics: "linear-gradient(135deg, #eab308, #facc15)",
      Implantology: "linear-gradient(135deg, #06b6d4, #22d3ee)",
      Periodontics: "linear-gradient(135deg, #14b8a6, #2dd4bf)",
      Preventive: "linear-gradient(135deg, #10b981, #34d399)",
      Emergency: "linear-gradient(135deg, #dc2626, #f87171)"
    };
    return colors[category] || "linear-gradient(135deg, #64748b, #94a3b8)";
  };

  if (loading) {
    return (
      <div className="procedure-launcher">
        <div className="proc-launcher-loading">
          <div className="spinner-small"></div>
          <span>Loading procedures...</span>
        </div>
      </div>
    );
  }

  if (Object.keys(groupedProcedures).length === 0) {
    const handleRetry = async () => {
      setLoading(true);
      try {
        await reloadProcedureRegistry();
        setGroupedProcedures(getGroupedProcedures());
      } catch {
        setGroupedProcedures({});
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="procedure-launcher">
        <div className="proc-launcher-error">
          Failed to load procedures. Check backend.
          <div className="proc-launcher-error-actions">
             <Button variant="primary" onClick={handleRetry}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="procedure-launcher">
      <div className="proc-launcher-header">
        <h3 className="proc-launcher-title">Launch Procedure</h3>
        {(activeTooth || activeTeeth.length > 0) && (
          <span className="active-tooth-badge">
            {activeTeeth.length > 1 ? `${activeTeeth.length} teeth` : `Tooth #${activeTooth || activeTeeth[0]}`}
          </span>
        )}
      </div>

      <input
        type="text"
        className="proc-search-input"
        placeholder="Search procedures..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      
      {currentRctSelectionType === "mixed" && (
        <div className="rct-warning">
          Please select only anterior teeth or only posterior teeth for RCT.
        </div>
      )}

      {pendingSessions.length > 0 && (
        <div className="pending-section">
          <h4 className="pending-title">
            ⏸ Pending Procedures
          </h4>
          <div className="pending-list">
            {pendingSessions.map((s) => (
              <Button
                key={s.id}
                variant="outline"
                size="md"
                onClick={() => onStart({ resumeSessionId: s.id })}
                className="proc-pending-btn"
              >
                🔄 Resume: {s.procedure_type || 'Procedure'} {s.tooth_no ? `(Tooth ${isoToFdi(s.tooth_no)})` : ''}
              </Button>
            ))}
          </div>
        </div>
      )}

      {plannedProcedures.length > 0 && (
        <div className="proc-category-section proc-category-section-planned">
          <h4 className="proc-category-title proc-category-title-planned">
            <span className="proc-category-icon">📋</span> Planned Procedures
          </h4>
          <div className="proc-launcher-grid">
            {plannedProcedures.map((proc) => {
              const fullProc = (Object.values(groupedProcedures).flat().find(p => p.master_id === proc.master_id) || {}) as ProcedureEntry;
              const targetTeeth = proc.tooth_no ? proc.tooth_no.split(',').map(t => t.trim()) : [];
              const normalizedSingleTooth = targetTeeth.length === 1 ? targetTeeth[0] : null;

              return (
                <button
                  key={`planned-${proc.plan_id}-${proc.item_id}`}
                  type="button"
                  className="proc-launcher-btn proc-launcher-btn-planned"
                  disabled={!!isLocked}
                  onClick={() => {
                    if (fullProc && fullProc.master_id) {
                      onStart({
                        master_id: proc.master_id,
                        targetTooth: normalizedSingleTooth,
                        targetTeeth: targetTeeth,
                        surface: null,
                        diagnosisCode: null
                      });
                    }
                  }}
                  title={`Start ${proc.procedure_name}`}
                >
                  <span className="proc-label">{proc.procedure_name}</span>
                  {proc.tooth_no && <span className="proc-hint">🦷 {proc.tooth_no}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="proc-launcher-categories">
        {Object.entries(filteredGrouped).map(([category, procedures]) => (
          <div key={category} className="proc-category-section">
            <h4 className="proc-category-title">
              <span className="proc-category-icon">{getCategoryIcon(category)}</span> {category}
            </h4>

            <div className="proc-launcher-grid">
              {procedures.map((proc) => {
                const targetSelector = getTargetSelector(proc.master_id);
                const requiresSingleTooth = proc.requires_tooth ?? targetSelector === "single_tooth";
                const requiresMultiTooth = targetSelector === "multi_tooth";
                const needsToothSelection = requiresSingleTooth || requiresMultiTooth;
                const hasToothSelected = (activeTooth != null && activeTooth !== "") || (activeTeeth != null && activeTeeth.length > 0);
                const isRctAnterior = (proc.master_id || "").toUpperCase().includes("RCT_ANTERIOR") || (proc.display_name || proc.name || "").toLowerCase().includes("anterior");
                const isRctPosterior = (proc.master_id || "").toUpperCase().includes("RCT_POSTERIOR") || (proc.display_name || proc.name || "").toLowerCase().includes("posterior");
                const rctType = isRctAnterior ? "anterior" : isRctPosterior ? "posterior" : null;
                const rctDisabled = rctType && (currentRctSelectionType === "mixed" || (currentRctSelectionType === "anterior" && rctType === "posterior") || (currentRctSelectionType === "posterior" && rctType === "anterior"));
                const disabledByToothRule = needsToothSelection ? !hasToothSelected : hasToothSelected;
                const isDisabled = !!isLocked || disabledByToothRule || !!rctDisabled;

                return (
                  <button
                    key={proc.master_id}
                    type="button"
                    className={`proc-launcher-btn ${isDisabled ? "is-disabled" : ""}`}
                    style={{ "--proc-bg": getCategoryColor(category) } as React.CSSProperties}
                    disabled={isDisabled}
                    onClick={() => handleStartProcedure(proc)}
                    title={
                      rctDisabled ? "Select only anterior or only posterior teeth" :
                      disabledByToothRule ? (needsToothSelection ? "Select a tooth first" : "Clear tooth selection to use this procedure") :
                      (proc.display_name || proc.name)
                    }
                  >
                    <span className="proc-label">{proc.display_name || proc.name}</span>
                    {requiresSingleTooth && <span className="proc-hint">🦷 Tooth</span>}
                    {requiresMultiTooth && <span className="proc-hint">🦷 Multiple teeth</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {toothPromptProc && (
        <div className="proc-tooth-prompt-overlay">
          <div className="proc-tooth-prompt">
            <h4>Tooth Required</h4>
            <p>
              <strong>{toothPromptProc.display_name || toothPromptProc.name}</strong> needs a tooth selection.
            </p>
            <input
              type="text"
              placeholder="e.g. 18 or 18, 22, 26"
              value={manualToothInput}
              onChange={(e) => setManualToothInput(e.target.value)}
              className="proc-tooth-manual-input"
            />
            <div className="proc-tooth-prompt-actions">
              <Button variant="ghost" onClick={() => setToothPromptProc(null)}>Cancel</Button>
              <Button variant="primary" onClick={handleToothPromptSubmit}>Confirm</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
